<template>
  <div class="admin">
    <div class="admin-header">
      <div class="admin-header__title">Admin home</div>
      <div v-if="adminName" class="admin-header__name">Signed in as {{ adminName }}</div>
    </div>

    <div class="admin-board">
      <div v-for="panel in panels" :key="panel.key" class="admin-cell" :class="cellClass(panel)">
        <div v-if="panel.key === 'alert_trend'" class="panel">
          <div class="panel__title">Alerts this week (SGT)</div>
          <ChartDataProvider timePeriod="7d" v-slot="{ chartData }">
            <AlertTrendChart :chartData="chartData" :chartOptions="weekChartOptions" />
          </ChartDataProvider>
        </div>
        <AlertsByCustomerWidget v-else-if="panel.key === 'alerts_by_customer'" />
        <OpsReportCard v-else :report="panel.key" :title="panel.title" :note="panel.note" />
      </div>
    </div>
  </div>
</template>

<script>
  import AlertTrendChart from './AlertTrendChart';
  import ChartDataProvider from './ChartDataProvider';
  import AlertsByCustomerWidget from './AlertsByCustomerWidget.vue';
  import OpsReportCard from './OpsReportCard.vue';

  // Panels in page order. The board has 12 columns. span is the width and must be
  // 3, 4, 6, 8 or 12. rows: 2 lets a tall panel sit beside two shorter ones.
  // Keys other than alert_trend and alerts_by_customer are ops_report names.
  const PANELS = [
    { key: 'alert_trend', span: 12 },
    { key: 'alerts_by_hour', span: 3, title: 'Alerts by hour', note: 'Last 24 hours, Singapore time. No detail: no rows in tblIncidentDetail.' },
    { key: 'checks_per_hour', span: 3, title: 'Checks per hour', note: 'Last 6 hours, Singapore time.' },
    { key: 'url_health', span: 3, title: 'URL health', note: 'Count and share of each health status.' },
    { key: 'polling_load', span: 3, title: 'Check load by interval', note: 'Expected checks per hour.' },
    { key: 'alerts_by_customer', span: 6, rows: 2 },
    { key: 'http_status_by_customer', span: 6, title: 'Non-200 alerts by customer', note: 'Last 24 hours. Top 20 customers.' },
    { key: 'email_bounces', span: 6, title: 'Email bounces', note: 'Last 24 hours.' },
    { key: 'engine_time', span: 12, title: 'Time per engine', note: 'Seconds from check start. Red: last hour is above the week average.' },
    { key: 'expiring_licences', span: 12, title: 'Licences expiring', note: 'Live URLs, expiry within 30 days.' },
    { key: 'urls_alerting_most_hours', span: 12, title: 'URLs that alert most hours', note: '20 or more hours in the last 24 hours.' },
    { key: 'overdue_urls', span: 12, title: 'Overdue URLs', note: 'Not checked within 3 polling intervals.' },
    { key: 'stuck_setup', span: 12, title: 'URLs stuck in setup', note: 'Incomplete setup for more than a day. First 200 rows.' },
  ];

  export default {
    name: 'AdminDashboard',
    components: {
      AlertTrendChart,
      ChartDataProvider,
      AlertsByCustomerWidget,
      OpsReportCard,
    },
    computed: {
      adminName() {
        const user = this.$store.state.user && this.$store.state.user.data;
        return (user && user.username) || '';
      },
    },
    methods: {
      cellClass(panel) {
        return {
          ['admin-cell--span-' + panel.span]: true,
          'admin-cell--rows-2': panel.rows === 2,
        };
      },
    },
    data() {
      return {
        panels: PANELS,
        weekChartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            display: true,
            position: 'top',
            labels: { boxWidth: 10, fontSize: 12, padding: 16, fontColor: '#323232' },
          },
          elements: {
            point: { radius: 2, hoverRadius: 4, hitRadius: 8 },
            line: { tension: 0.15, borderWidth: 2 },
          },
          tooltips: { mode: 'index', intersect: false },
          layout: { padding: { left: 16, right: 16, top: 8, bottom: 12 } },
          scales: {
            yAxes: [{
              ticks: { beginAtZero: true, fontSize: 12, fontColor: '#323232', maxTicksLimit: 6, padding: 6 },
              gridLines: { color: '#f0f0f0', zeroLineColor: '#e8e8e8' },
            }],
            xAxes: [{
              ticks: {
                fontSize: 12,
                fontColor: '#323232',
                maxRotation: 0,
                minRotation: 0,
                autoSkip: false,
                padding: 6,
              },
              gridLines: { display: false },
            }],
          },
        },
      };
    },
  };
</script>

<style lang="scss" scoped>
  .admin {
    background: #f4f5f7;
    min-height: 100%;
    padding-bottom: 8px;
    font-family: Roboto, sans-serif;
  }

  .admin-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px 6px;
  }

  .admin-header__title {
    font-size: 18px;
    font-weight: 700;
    color: #323232;
  }

  .admin-header__name {
    font-size: 12px;
    color: #565656;
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 12px;
    padding: 2px 8px;
  }

  .admin-board {
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    gap: 8px;
    align-items: start;
    padding: 0 12px 12px;
  }

  .admin-cell {
    min-width: 0;
  }

  @each $span in 3, 4, 6, 8, 12 {
    .admin-cell--span-#{$span} {
      grid-column: span $span;
    }
  }

  .admin-cell--rows-2 {
    grid-row: span 2;
  }

  @media (max-width: 1100px) {
    .admin-cell--span-3,
    .admin-cell--span-4 {
      grid-column: span 6;
    }

    .admin-cell--span-8 {
      grid-column: span 12;
    }
  }

  @media (max-width: 720px) {
    .admin-cell {
      grid-column: span 12;
      grid-row: auto;
    }
  }

  .panel {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    box-shadow: 0 1px 2px rgba(21, 27, 34, 0.05);
    padding: 8px 10px 2px;
    min-width: 0;
  }

  .panel__title {
    font-size: 14px;
    font-weight: 600;
    color: #323232;
    margin-bottom: 4px;
  }

  .panel ::v-deep .chart-filters {
    display: none;
  }

  .panel ::v-deep .alert-trend-section__chart {
    position: relative;
    height: 480px;
    width: 100%;
    margin-bottom: 0 !important;
  }
</style>
